import React, { useRef, useState, useEffect, useMemo, useCallback } from "react";
import {
  ChevronLeft,
  ChevronRight,
  ArrowRight,
  ArrowUpRight,
  HeartPulse,
  GraduationCap,
  Landmark,
  Building2,
  ShieldCheck,
  Cpu,
} from "lucide-react";
import { Link } from "react-router-dom";
import healthcareImg from "../assets/healthcare.jpg";
import educationImg from "../assets/education.jpg";
import bankingImg from "../assets/banking.jpg";
import realEstateImg from "../assets/realestate.jpg";
import insuranceImg from "../assets/insurance.jpeg";
import technologyImg from "../assets/Technology.jpeg";

// Sectors data with lead management focus
const SECTOR_DATA = [
  {
    id: "healthcare",
    label: "Healthcare",
    icon: HeartPulse,
    highlights: ["Patient inquiries", "Appointment follow-ups", "Diagnostic leads"],
    route: "/solutions/healthcare",
    image: healthcareImg,
  },
  {
    id: "education",
    label: "Education",
    icon: GraduationCap,
    highlights: ["Counsellor", "Admission follow-ups", "Auto communication"],
    route: "/solutions/education",
    image: educationImg,
  },
  {
    id: "banking",
    label: "Banking",
    icon: Landmark,
    highlights: ["Loan inquiries", "DSA partners", "Branch conversion"],
    route: "/solutions/banking-finance",
    image: bankingImg,
  },
  {
    id: "realestate",
    label: "Real Estate",
    icon: Building2,
    highlights: ["Site visits", "Budget-based scoring", "Auto follow-ups"],
    route: "/solutions/real-estate",
    image: realEstateImg,
  },
  {
    id: "insurance",
    label: "Insurance",
    icon: ShieldCheck,
    highlights: ["Policy inquiries", "Agent performance", "Renewal reminders"],
    route: "/solutions/insurance",
    image: insuranceImg,
  },
  {
    id: "technology",
    label: "Technology",
    icon: Cpu,
    highlights: ["Demo requests", "Engagement scoring", "Nurture sequences"],
    route: "/solutions/technology",
    image: technologyImg,
  },
];

// "All Sectors" tab + one tab per sector
const tabs = [{ id: "all", label: "All Sectors" }, ...SECTOR_DATA];

const SectorCard = ({ sector }) => {
  const Icon = sector.icon;
  return (
    <Link
      to={sector.route}
      aria-label={`${sector.label} lead management details`}
      className="group flex h-full w-[240px] flex-shrink-0 snap-start flex-col overflow-hidden rounded-xl border border-[#48247f]/10 bg-white shadow-[0_6px_20px_-10px_rgba(72,36,127,0.2)] transition-all duration-300 hover:-translate-y-1.5 hover:border-[#702cb0]/40 hover:shadow-[0_16px_32px_-12px_rgba(72,36,127,0.35)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#702cb0] focus-visible:ring-offset-2 motion-reduce:transition-none motion-reduce:hover:translate-y-0 sm:w-[270px]"
    >
      {/* Image */}
      <div className="relative h-36 w-full overflow-hidden sm:h-40">
        <img
          src={sector.image}
          alt={`${sector.label} lead management software workflow`}
          width="540"
          height="320"
          loading="lazy"
          decoding="async"
          className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-110 motion-reduce:transition-none motion-reduce:group-hover:scale-100"
        />

        {/* Tinted overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-[#28194f]/90 via-[#28194f]/25 to-transparent transition-opacity duration-300" />
        <div className="absolute inset-0 bg-[#702cb0]/0 mix-blend-multiply transition-colors duration-500 group-hover:bg-[#702cb0]/25" />

        {/* Sector icon badge */}
        <div className="absolute left-3 top-3 flex h-9 w-9 items-center justify-center rounded-lg bg-white/90 text-[#48247f] shadow-md backdrop-blur-md transition-all duration-300 group-hover:-rotate-6 group-hover:bg-[#12dfbd] group-hover:text-[#102b37]">
          <Icon className="h-4 w-4" />
        </div>

        {/* Arrow bubble (also shown on keyboard focus) */}
        <div className="absolute right-3 top-3 flex h-8 w-8 translate-y-1 items-center justify-center rounded-full bg-white/90 text-[#48247f] opacity-0 shadow-md transition-all duration-300 group-hover:translate-y-0 group-hover:opacity-100 group-focus-visible:translate-y-0 group-focus-visible:opacity-100">
          <ArrowUpRight className="h-3.5 w-3.5" />
        </div>

        {/* Title over image */}
        <div className="absolute inset-x-0 bottom-0 p-3.5">
          <p className="mb-0.5 text-[11px] font-semibold uppercase tracking-[0.12em] text-[#12dfbd]">
            Lead Management
          </p>
          <h3 className="text-lg font-light tracking-tight text-white">
            {sector.label}
          </h3>
        </div>
      </div>

      {/* Content */}
      <div className="flex flex-1 flex-col p-4">
        {/* Key capabilities */}
        <ul className="flex flex-1 flex-wrap content-start gap-1.5">
          {sector.highlights.map((h) => (
            <li
              key={h}
              className="inline-flex items-center rounded-full bg-[#f1ecfa] px-2.5 py-1 text-xs font-medium text-[#48247f] transition-colors duration-300 group-hover:bg-[#e3faf5]"
            >
              {h}
            </li>
          ))}
        </ul>

        {/* CTA */}
        <span className="mt-4 inline-flex min-h-[44px] w-full items-center justify-center gap-1.5 rounded-lg bg-gradient-to-r from-[#48247f] to-[#702cb0] px-4 py-2.5 text-xs font-semibold text-white shadow-sm shadow-[#48247f]/20 transition-all duration-300 group-hover:shadow-md group-hover:shadow-[#48247f]/40 sm:text-sm">
          Explore {sector.label}
          <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
        </span>
      </div>
    </Link>
  );
};

const ServiceHome = () => {
  const scrollRef = useRef(null);
  const [activeTab, setActiveTab] = useState("all");
  const [edges, setEdges] = useState({ left: false, right: false });

  const filteredSectors = useMemo(
    () =>
      activeTab === "all"
        ? SECTOR_DATA
        : SECTOR_DATA.filter((s) => s.id === activeTab),
    [activeTab]
  );

  // Work out whether there is more content to scroll to on either side
  const updateEdges = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setEdges({
      left: el.scrollLeft > 4,
      right: el.scrollLeft + el.clientWidth < el.scrollWidth - 4,
    });
  }, []);

  // Reset scroll on tab change, and keep arrow states in sync on resize
  useEffect(() => {
    scrollRef.current?.scrollTo({ left: 0 });
    updateEdges();
    window.addEventListener("resize", updateEdges);
    return () => window.removeEventListener("resize", updateEdges);
  }, [activeTab, updateEdges]);

  // Scroll by roughly one card, and skip the smooth animation for reduced motion
  const scroll = (direction) => {
    const el = scrollRef.current;
    if (!el) return;
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const step = Math.max(240, Math.round(el.clientWidth * 0.8));
    el.scrollBy({
      left: direction === "left" ? -step : step,
      behavior: reduced ? "auto" : "smooth",
    });
  };

  // Arrow-key navigation between tabs (standard tablist behaviour)
  const onTabKeyDown = (e, index) => {
    let next = null;
    if (e.key === "ArrowRight") next = (index + 1) % tabs.length;
    if (e.key === "ArrowLeft") next = (index - 1 + tabs.length) % tabs.length;
    if (e.key === "Home") next = 0;
    if (e.key === "End") next = tabs.length - 1;
    if (next === null) return;
    e.preventDefault();
    setActiveTab(tabs[next].id);
    document.getElementById(`sector-tab-${tabs[next].id}`)?.focus();
  };

  return (
    <section
      id="solutions"
      aria-labelledby="solutions-heading"
      className="relative isolate w-full overflow-hidden bg-[#f6f3fc] font-sans text-[#28194f]"
    >
      {/* Soft background accents + subtle dot pattern */}
      <div className="pointer-events-none absolute -left-24 -top-24 h-96 w-96 rounded-full bg-[#702cb0]/10 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-24 -right-24 h-96 w-96 rounded-full bg-[#12dfbd]/15 blur-3xl" />
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.35]"
        style={{
          backgroundImage: "radial-gradient(#48247f22 1px, transparent 1px)",
          backgroundSize: "22px 22px",
        }}
      />

      <div className="relative z-10 mx-auto w-full max-w-[1500px] px-4 py-14 sm:px-10 sm:py-16 lg:px-14 lg:py-20 xl:px-20">
        {/* Header */}
        <div className="flex flex-col gap-8 md:flex-row md:items-end md:justify-between">
          <div className="max-w-3xl text-center md:text-left">
            <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-[#48247f]/15 bg-white px-3 py-1 text-xs font-medium text-[#48247f] shadow-sm">
              <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-[#12dfbd]" />
              Industry Solutions
            </div>

            <h2
              id="solutions-heading"
              className="text-balance text-[26px] font-light leading-[1.25] tracking-tight sm:text-[34px] lg:text-[38px] xl:text-[42px]"
            >
              Smart Lead Management{" "}
              <span className="bg-gradient-to-r from-[#48247f] to-[#702cb0] bg-clip-text font-normal text-transparent">
                Solution
              </span>
            </h2>

            <p className="mx-auto mt-5 max-w-[640px] text-[15px] font-normal leading-[1.75] text-[#3b2a63] sm:text-lg md:mx-0 md:border-l-4 md:border-[#12dfbd] md:pl-5">
              Industry-specific lead management solutions designed to capture,
              track, and convert more leads.{" "}
              <span className="font-medium text-[#28194f]">
                Choose your sector and transform your sales pipeline today.
              </span>
            </p>
          </div>

          <div className="hidden gap-3 md:flex">
            {[
              ["left", ChevronLeft],
              ["right", ChevronRight],
            ].map(([dir, Icon]) => (
              <button
                key={dir}
                type="button"
                onClick={() => scroll(dir)}
                disabled={!edges[dir]}
                aria-label={`Scroll sectors ${dir}`}
                className="flex h-11 w-11 items-center justify-center rounded-full border border-[#48247f]/15 bg-white text-[#48247f] shadow-sm transition-all duration-300 hover:bg-[#48247f] hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#702cb0] disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-white disabled:hover:text-[#48247f]"
              >
                <Icon className="h-5 w-5" />
              </button>
            ))}
          </div>
        </div>

        {/* Tabs */}
        <div
          role="tablist"
          aria-label="Filter by sector"
          className="-mx-4 mb-8 mt-10 flex gap-2 overflow-x-auto px-4 pb-1 [scrollbar-width:none] sm:mx-0 sm:flex-wrap sm:overflow-visible sm:px-0 [&::-webkit-scrollbar]:hidden"
        >
          {tabs.map((tab, i) => {
            const active = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                id={`sector-tab-${tab.id}`}
                type="button"
                role="tab"
                aria-selected={active}
                aria-controls="sector-panel"
                tabIndex={active ? 0 : -1}
                onClick={() => setActiveTab(tab.id)}
                onKeyDown={(e) => onTabKeyDown(e, i)}
                className={`min-h-[44px] flex-shrink-0 whitespace-nowrap rounded-full border px-4 py-2 text-xs font-medium transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#702cb0] focus-visible:ring-offset-2 sm:text-sm ${
                  active
                    ? "border-transparent bg-gradient-to-r from-[#48247f] to-[#702cb0] text-white shadow-lg shadow-[#48247f]/25"
                    : "border-[#48247f]/15 bg-white text-slate-700 hover:border-[#702cb0]/50 hover:text-[#48247f]"
                }`}
              >
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Sector cards */}
        <div
          id="sector-panel"
          role="tabpanel"
          aria-labelledby={`sector-tab-${activeTab}`}
          ref={scrollRef}
          onScroll={updateEdges}
          className="-mx-4 snap-x snap-mandatory overflow-x-auto scroll-pl-4 px-4 pb-8 pt-2 [scrollbar-width:none] sm:-mr-10 sm:ml-0 sm:scroll-pl-0 sm:pl-0 sm:pr-10 lg:-mr-14 lg:pr-14 xl:-mr-20 xl:pr-20 [&::-webkit-scrollbar]:hidden"
        >
          {filteredSectors.length > 0 ? (
            <ul className="flex w-max items-stretch gap-4 sm:gap-5">
              {filteredSectors.map((sector) => (
                <li key={sector.id} className="flex">
                  <SectorCard sector={sector} />
                </li>
              ))}
            </ul>
          ) : (
            <div className="flex items-center justify-center rounded-2xl border border-dashed border-[#48247f]/20 bg-white px-4 py-16">
              <div className="text-center">
                <p className="mb-2 text-lg font-light">No sectors found</p>
                <p className="text-sm font-light text-slate-500">
                  Try selecting a different filter
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default ServiceHome;